import { InjectionToken, NgModule } from '@angular/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import {
  ApolloClientOptions,
  InMemoryCache,
  ApolloLink,
  fromPromise,
  ApolloClient,
} from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { secureRandom } from './shared/utils/secure-random';
import { MUTATION_REFRESHTOKEN } from './@core/graphql/mutations/refreshtoken.mutation';
import { Router } from '@angular/router';
import { TokenService } from './@core/services/token.service';
import { getMainDefinition, Observable } from '@apollo/client/utilities';
import { OperationDefinitionNode } from 'graphql';
import { EnvService } from '@environment';
import { environment } from '../environments/environment';
import { WebSocketLink } from '@apollo/client/link/ws';
import { HttpHeaders } from '@angular/common/http';
import { version } from '@root/version';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { Subject } from 'rxjs';

/**
 * Injection token for a Subject that emits `true` each time the WebSocket
 * connection is re-established after a disconnect.
 */
export const WS_RECONNECTED$ = new InjectionToken<Subject<boolean>>(
  'WS_RECONNECTED$',
);

/** Singleton subject — created once, shared via DI. */
const wsReconnected$ = new Subject<boolean>();
//const uri = "http://162.243.161.180:3000/graphql";
//const uri = "http://localhost:3000/graphql";

export function createApollo(
  httpLink: HttpLink,
  tokenService: TokenService,
  router: Router,
  //sharedEnv: EnvService
): ApolloClientOptions<any> {
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8',
    },
  }));

  const auth = setContext((operation, context) => {
    const token: any = tokenService.token;
    if ((<any>operation).query.definitions[0].operation === 'subscription') {
      return {
        connectionParams: () => {
          const token: any = tokenService.token;
          return {
            headers: {
              authorization: `BEARER ${token?.accessToken || ''}`,
            },
          };
        },
      };
    }
    return {
      headers: {
        Authorization: `BEARER ${token?.accessToken || ''}`,
      },
    };
  });

  let isRefreshing = false;
  let pendingRequests = [];

  const resolvePendingRequests = () => {
    pendingRequests.forEach((callback) => callback());
    pendingRequests = [];
  };

  // Authentication mutations must never be run through the refresh-and-retry
  // flow below: they ARE the thing that produces a session. Retrying them
  // silently swallows a legitimate "Unable to sign in" rejection, which left
  // the SSO callback spinner hanging forever instead of surfacing the error.
  const AUTH_OPERATIONS = new Set(['azureLogin', 'login', 'refreshToken']);

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors && !AUTH_OPERATIONS.has(operation.operationName)) {
        for (const err of graphQLErrors) {
          const e = err?.extensions?.code || err.message;
          switch (e) {
            case 'Unauthorized':
            case 'UNAUTHENTICATED':
              // eslint-disable-next-line no-case-declarations
              let forward$: Observable<any>;
              if (!isRefreshing) {
                isRefreshing = true;
                const oldToken = tokenService.token;
                forward$ = fromPromise(
                  client
                    .mutate({
                      mutation: MUTATION_REFRESHTOKEN,
                      variables: { token: oldToken?.refreshToken },
                    })
                    .then(({ data }) => {
                      const accessToken = <any>data;
                      tokenService.setToken(accessToken?.refreshToken);
                      const oldHeaders = operation.getContext().headers;
                      operation.setContext({
                        headers: {
                          ...oldHeaders,
                          Authorization: `Bearer ${accessToken}`,
                        },
                      });

                      // Force WebSocket reconnection with new token
                      // We use terminate() if available (v5.12+) or access the socket directly to close it.
                      // This forces a "clean" disconnect that triggers the client's reconnection logic.
                      try {
                        if ((wsClient as any).terminate) {
                          (wsClient as any).terminate();
                        } else if (activeSocket) {
                          // Close with a code other than 1000/1001 to trigger auto-reconnect
                          // 4205 is arbitrary, just needs to be non-standard normal closure
                          activeSocket.close(4205, 'Token Refresh');
                        } else {
                          console.warn(
                            'Could not force WebSocket reconnection: No active socket found.',
                          );
                        }
                      } catch (e) {
                        console.error('Error restarting websocket client', e);
                      }

                      resolvePendingRequests();
                      return accessToken;
                    })
                    .catch((error) => {
                      pendingRequests = [];
                      router.navigate(['/auth/login']);
                      // Handle token refresh errors e.g clear stored tokens, redirect to login, ...
                      return;
                    })
                    .finally(() => {
                      isRefreshing = false;
                    }),
                ).filter((value) => Boolean(value));
              } else {
                // Will only emit once the Promise is resolved

                forward$ = fromPromise(
                  new Promise((resolve) => {
                    pendingRequests.push(() => resolve(null));
                  }),
                );
              }

              return forward$.flatMap(() => forward(operation));
          }
        }
      }
      if (networkError) {
        console.log(`[Network error]: ${JSON.stringify(networkError)}`);
        // if you would also like to retry automatically on
        // network errors, we recommend that you use
        // apollo-link-retry
      }
    },
  );

  const http = httpLink.create({
    uri: environment.graphQL,
    headers: new HttpHeaders({
      'X-Client-Version': `Stack Web ${version || 'unknown'}`,
    }),
  });
  const token = tokenService.token;

  // Create a WebSocket link:
  // const ws = new WebSocketLink({
  //   uri: environment.graphQLWS,
  //   options: {
  //     reconnect: true,
  //     connectionParams: () => {
  //       const token: any = tokenService.token;
  //       return {
  //         authorization: `BEARER ${token?.accessToken || ''}`,
  //       };
  //     },
  //   },
  // });

  let activeSocket: any;
  let wasDisconnected = false;
  const wsClient = createClient({
    url: environment.graphQLWS,
    connectionParams: () => {
      const token: any = tokenService.token;
      return {
        Authorization: `BEARER ${token?.accessToken || ''}`,
      };
    },
    retryAttempts: Infinity,
    retryWait: async function randomisedExponentialBackoff(retries) {
      // Exponential backoff capped at 30 seconds
      const delay = Math.min(1000 * 2 ** retries + secureRandom() * 100, 30000);
      return new Promise((resolve) => setTimeout(resolve, delay));
    },
    on: {
      connected: (socket) => {
        activeSocket = socket;
        if (wasDisconnected) {
          // We just reconnected after a disconnect — notify subscribers
          wsReconnected$.next(true);
        }
        wasDisconnected = false;
      },
      closed: (event) => {
        activeSocket = null;
        wasDisconnected = true;
      },
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  // ADDED: Store the WebSocket client reference
  //wsClient = (ws as any).subscriptionClient;

  const link = ApolloLink.from([
    errorLink,
    ApolloLink.concat(
      auth,
      ApolloLink.split(
        // split based on operation type
        ({ query }) => {
          const { kind, operation } = <OperationDefinitionNode>(
            getMainDefinition(query)
          );
          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        http,
      ),
      //http,
    ),
  ]);

  const cache = new InMemoryCache({
    typePolicies: {
      SchedulerTimeSheetEvent: { keyFields: ['id'] },
      SchedulerServiceVisitEvent: { keyFields: ['serviceVisitId'] },
    },
  });
  const client = new ApolloClient({
    link,
    cache,
  });

  return {
    link,
    cache,
  };
}

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, TokenService, Router],
    },
    { provide: WS_RECONNECTED$, useValue: wsReconnected$ },
    EnvService,
  ],
})
export class GraphQLModule {}
